import { useState } from 'react';
import { Select, SelectProps, BaseOption } from './select';
import { useDebounce } from '@/hooks/use-debounce';
import { Loader } from '../loaders/loader';

export interface SearchableSelectProps<T extends BaseOption> extends Omit<
  SelectProps<T>,
  'onInputChange' | 'inputValue'
> {
  onSearch: (term: string) => void;
  debounceTime?: number;
  isLoading?: boolean;
  menuIsOpen?: boolean;
}

export function SearchableSelect<T extends BaseOption>({
  onSearch,
  debounceTime = 200,
  formatOptionLabel,
  isLoading,
  menuIsOpen,
  ...props
}: SearchableSelectProps<T>) {
  const [inputValue, setInputValue] = useState('');

  const { isReady } = useDebounce(() => onSearch(inputValue), debounceTime, [inputValue]);

  const defaultFormatOptionLabel = (option: T) => {
    return highlightSubstring(option.label, inputValue);
  };

  return (
    <Select
      {...props}
      inputValue={inputValue}
      onInputChange={(val) => setInputValue(val)}
      formatOptionLabel={formatOptionLabel || defaultFormatOptionLabel}
      removeFilterOption
      menuIsOpen={menuIsOpen}
      noOptionsMessage={() =>
        !isReady() || isLoading ? <Loader type="small" textKey="common.searching" /> : undefined
      }
    />
  );
}

export function highlightSubstring(text: string, substring: string) {
  if (!substring) {
    return <span>{text}</span>;
  }

  const parts = text.split(new RegExp(`(${substring})`, 'gi'));

  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === substring.toLowerCase() ? (
          <strong key={index}>{part}</strong>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </>
  );
}
